// Meetings (SMeetings, SPhoneMeetings, SPhoneMeeting): every meeting and workshop with its recording, minutes and
// actions; a meeting opens with the player, Summary / Transcript / Ask about it, the actions and meetings to set up,
// and, before it happens, the agenda, the prep and what happened last time. "Log a meeting" files minutes and actions
// by hand, saved directly.
import { SheetAiButton } from '../assistant/AiButton';
import { useAiScope } from '../assistant/AiButton';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import type { MeetingDetail, MeetingRow } from '@dpw/shared';
import { api } from '../api';
import { usePhone } from '../lib/media';
import { useActivity, useChange, useSpaces, useTeammates } from '../lib/queries';
import { gbDate } from '../lib/format';
import { ErrorNote, Loading } from '../ui/bits';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/toast';
import { AskMeeting, MINUTES_STATE, MinutesBody, Player, Transcript, clock } from '../meetings/parts';
import { P, PSheet, Section, Seg, Tag, Title, TopBar } from '../phone/kit';

type When = 'all' | 'upcoming' | 'past';
const unnamed = (r: { speakers: { confirmed: boolean }[] }) => { const n = r.speakers.filter(s => !s.confirmed).length; return n ? `${n} ${n === 1 ? 'speaker' : 'speakers'}` : ''; };
const whenText = (m: Pick<MeetingRow, 'startsAt' | 'endsAt'>) => {
  if (!m.startsAt) return '';
  const s = new Date(m.startsAt), e = m.endsAt ? new Date(m.endsAt) : null;
  const t = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' });
  return `${gbDate(s, { weekday: 'short', day: 'numeric', month: 'short' })}, ${t(s)}${e ? ` to ${t(e)}` : ''}`;
};
const recText = (m: MeetingRow) => (m.recordingSecs ? clock(m.recordingSecs) : m.past ? '' : m.today ? 'Today' : 'Not yet');

export function MeetingsPage() {
  // A meeting's page: the AI button looks at that meeting (its transcript and minutes).
  const { id } = useParams();
  const m = useActivity(id ?? '', !!id);
  useAiScope(id && m.data ? { spaceId: m.data.spaceId, trackId: m.data.trackId, activityId: m.data.id, label: m.data.title } : null);
  return usePhone() ? <PhoneMeetings /> : <DesktopMeetings />;
}

function useMeetings(when: When, spaceId: string | null) {
  return useQuery({ queryKey: ['meetings', when, spaceId], queryFn: () => api.meetings.list({ when, ...(spaceId ? { spaceId } : {}) }), refetchInterval: 30_000 });
}

function DesktopMeetings() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [when, setWhen] = useState<When>('all');
  const [space, setSpace] = useState<string | null>(null);
  const spaces = useSpaces();
  const q = useMeetings(when, space);
  const logging = params.get('log') === '1';
  return (
    <div className="mtg col" style={{ gap: 14 }}>
      <div className="hdr"><div className="htl"><h1>Meetings</h1><span className="xs mut">Recordings, transcripts, minutes and what came out of each meeting</span></div>
        <div className="hact"><button type="button" className="bt" onClick={() => setParams({ log: '1' })}>Log a meeting</button><Link className="bt pri" to="/record">Record a meeting</Link></div></div>
      <div className="filters">
        <div className="seg2" role="group" aria-label="When">{(['all', 'upcoming', 'past'] as When[]).map(w => <button key={w} type="button" className={when === w ? 'on' : ''} aria-pressed={when === w} onClick={() => setWhen(w)}>{w[0]!.toUpperCase() + w.slice(1)}</button>)}</div>
        <div className="seg2" role="group" aria-label="Space"><button type="button" className={!space ? 'on' : ''} aria-pressed={!space} onClick={() => setSpace(null)}>All</button>
          {(spaces.data ?? []).map(s => <button key={s.id} type="button" className={space === s.id ? 'on' : ''} aria-pressed={space === s.id} onClick={() => setSpace(s.id)}>{s.name}</button>)}</div>
      </div>
      <div className="panel" style={{ padding: 6, overflowX: 'auto' }}>
        {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : (
          <table className="mt" aria-label="Meetings">
            <thead><tr><th>When</th><th>Meeting</th><th>Track</th><th>Recording</th><th>Minutes</th><th>Actions</th></tr></thead>
            <tbody>
              {(q.data ?? []).map(m => (
                <tr key={m.id} className="mrow" data-meeting={m.title} onClick={() => nav(`/meetings/${m.id}`)}>
                  <td className="xs mono" style={{ whiteSpace: 'nowrap' }}>{whenText(m).replace(/ to \d\d:\d\d$/, '')}</td>
                  <td><button type="button" className="mlink" onClick={e => { e.stopPropagation(); nav(`/meetings/${m.id}`); }}>{m.title}</button><div className="xs mut">{m.with.join(', ') || m.spaceName}</div></td>
                  <td className="xs"><span className="tdot" style={{ ['--tc' as string]: m.trackColor }} />{m.trackName}</td>
                  <td className="xs mono">{recText(m)}</td>
                  <td><span className="st" style={{ ['--sc' as string]: MINUTES_STATE[m.minutes][1] }}><i />{MINUTES_STATE[m.minutes][0]}</span></td>
                  <td className="xs mono">{m.actions ?? ''}</td>
                </tr>
              ))}
              {!q.data?.length && <tr><td colSpan={6} className="sm mut" style={{ padding: 20 }}>No meetings here. Add a meeting to a track, or record one now.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <AnimatePresence>
        {id && <Sheet key={id} crumbs={<span>Meetings</span>} label="Meeting" width={760} onClose={() => nav('/meetings')} tools={<SheetAiButton />}><MeetingView id={id} /></Sheet>}
        {logging && <Sheet key="log" crumbs={<span>Meetings</span>} label="Log a meeting" width={620} onClose={() => setParams({})}><LogMeeting onDone={a => { setParams({}); nav(`/meetings/${a}`); }} /></Sheet>}
      </AnimatePresence>
    </div>
  );
}

/** ?at=SECONDS (a source in the assistant's answer): play the meeting from that moment. */
function useSeekParam(onSeek: (at: number) => void) {
  const [params] = useSearchParams();
  const at = params.get('at');
  const seek = useRef(onSeek);
  seek.current = onSeek;
  useEffect(() => { const n = Number(at); if (at !== null && Number.isFinite(n) && n >= 0) seek.current(n); }, [at]);
}

/** One meeting (the desktop sheet). */
function MeetingView({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['meeting', id], queryFn: () => api.meetings.get(id), refetchInterval: r => (r.state.data?.minutes === 'processing' ? 4000 : false) });
  const [tab, setTab] = useState<'sum' | 'tr' | 'ask'>('sum');
  const [seek, setSeek] = useState<{ at: number; n: number } | null>(null);
  const onSeek = (at: number) => setSeek(s => ({ at, n: (s?.n ?? 0) + 1 }));
  useSeekParam(onSeek);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const m = q.data;
  const rec = m.recording;
  const minutes = rec?.minutes ?? null;
  return (
    <div className="col" style={{ gap: 14 }} data-meeting-detail={m.title}>
      <div className="xs mut">{m.spaceName} / {m.trackName} / {m.type === 'workshop' ? 'Workshop' : 'Meeting'} · <Link to={`/activities/${m.id}`}>Open activity</Link></div>
      <div className="rowc btwc"><h2 style={{ margin: 0 }}>{m.title}</h2><span className="st" style={{ ['--sc' as string]: MINUTES_STATE[m.minutes][1] }}><i />{MINUTES_STATE[m.minutes][0]}</span></div>
      <div className="bl" style={{ gridTemplateColumns: '90px 1fr' }}>
        <b>When</b><span>{whenText(m)}</span>{m.location ? <><b>Where</b><span>{m.location}</span></> : null}
        <b>With</b><span>{m.with.join(', ') || 'Not listed'}</span><b>Owner</b><span>{m.owner?.name ?? 'Nobody'}</span>
      </div>
      {m.past || rec ? <>
        {rec && <Player src={rec.hasAudio ? api.recordings.audioUrl(rec.id) : null} secs={rec.durationSecs ?? 0} marks={(minutes?.topics ?? []).map(t => t.at)} seekTo={seek} />}
        {rec && <div className="xs mut">Recorded in the app by {rec.recordedBy.name}{rec.durationSecs ? `, ${clock(rec.durationSecs)}` : ''}</div>}
        {m.minutes === 'wait' && rec?.actionId && <div className="mini" style={{ background: 'var(--amb-bg)' }}><b className="sm">Not filed yet</b><div className="xs">Review the minutes and choose where each action goes.{unnamed(rec) ? ` ${unnamed(rec)} not confirmed: name them in the transcript.` : ''}</div><div><Link className="bt sm" to={`/requests?id=${rec.actionId}`}>Review in Requests</Link></div></div>}
        {m.minutes === 'processing' && <div className="mini ai xs">The assistant is writing the minutes.</div>}
        {(rec?.lines.length || minutes || m.filed.length) ? <>
          <div className="seg2" role="tablist" aria-label="Meeting">
            {([['sum', 'Summary'], ['tr', 'Transcript'], ['ask', 'Ask about it']] as const).map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l}</button>)}
          </div>
          {tab === 'sum' && <Summary m={m} onSeek={onSeek} />}
          {tab === 'tr' && <Transcript lines={rec?.lines ?? []} rec={rec} canName={m.canRecord} onSeek={onSeek} />}
          {tab === 'ask' && <AskMeeting meetingId={m.id} title={m.title} chips={['Summarise the outcome', 'What did we decide?', 'Who owns what?']} />}
        </> : <div className="mini xs">Not recorded. <Link to={`/meetings?log=1&activity=${m.id}`}>Log the minutes by hand</Link>.</div>}
      </> : <Upcoming m={m} />}
    </div>
  );
}

function Summary({ m, onSeek }: { m: MeetingDetail; onSeek: (at: number) => void }) {
  const minutes = m.recording?.minutes;
  const waiting = m.minutes === 'wait';
  return (
    <div className="col" style={{ gap: 16 }}>
      {minutes ? <MinutesBody m={minutes} onSeek={onSeek} /> : m.filed[0] ? <div className="mini"><span className="k">Minutes</span><div className="sm" style={{ whiteSpace: 'pre-line', lineHeight: 1.55 }}>{m.filed[0].body}</div></div> : null}
      {waiting && minutes && minutes.actions.length > 0 && <div className="sec2"><span className="k">Actions</span>
        {minutes.actions.map(a => <div className="ar" key={a.title}><span>{a.title}<div className="xs mut">Will go to: {m.spaceName} · {m.trackName} · {a.type === 'deliverable' ? 'Deliverable' : 'Task'}</div></span><span className="xs mono">{a.dueDate ? gbDate(new Date(`${a.dueDate}T12:00:00Z`), { weekday: 'short', day: 'numeric', month: 'short' }) : ''}</span></div>)}
      </div>}
      {!waiting && m.actionsFiled.length > 0 && <div className="sec2"><span className="k">Actions</span>
        {m.actionsFiled.map(a => <div className="ar" key={a.id}><span><Link to={`/activities/${a.id}`}>{a.title}</Link><div className="xs mut">Filed: {m.spaceName} · {m.trackName} · {a.type[0]!.toUpperCase() + a.type.slice(1)}</div></span><span className="xs mono">{a.owner?.initials ?? ''} {a.dueDate ? gbDate(new Date(`${a.dueDate}T12:00:00Z`), { day: 'numeric', month: 'short' }) : ''}</span></div>)}
      </div>}
      {minutes && minutes.setup.length > 0 && <div className="sec2"><span className="k">Meetings to set up</span>
        {minutes.setup.map(s => <div className="ar" key={s.title}><span>{s.title}<div className="xs mut">{s.detail}</div></span><span className="xs mut">{waiting ? 'A reminder when filed' : 'Reminder set'}</span></div>)}
      </div>}
    </div>
  );
}

function Upcoming({ m }: { m: MeetingDetail }) {
  const done = m.prep.filter(p => p.done).length;
  return (
    <div className="col" style={{ gap: 14 }}>
      {m.agenda.length > 0 && <div className="sec2"><span className="k">Agenda</span>{m.agenda.map(a => <div className="topic" key={a}><span>{a}</span></div>)}</div>}
      {m.prep.length > 0 && <div className="sec2"><span className="k">Prep · {done} of {m.prep.length} ready</span>{m.prep.map(p => <div className="topic" key={p.title}><span className={`chk${p.done ? ' on' : ''}`} aria-hidden="true" /><span>{p.title}</span></div>)}</div>}
      {m.last && <div className="sec2"><span className="k">Last time</span><div className="sm"><Link to={`/meetings/${m.last.id}`}>{m.last.title}</Link>: {m.last.summary}</div></div>}
      <div className="rowc">
        {m.canRecord && <Link className="bt pri" to={`/record?activity=${m.id}`}>Record on the day</Link>}
        <button type="button" className="bt" disabled title="A later phase">Send the notetaker</button>
      </div>
    </div>
  );
}

/** Log a meeting by hand: an existing meeting or a new one, its minutes, and the actions that come out of it. */
function LogMeeting({ onDone, phone }: { onDone: (activityId: string) => void; phone?: boolean }) {
  const [params] = useSearchParams();
  const toast = useToast();
  const past = useQuery({ queryKey: ['meetings', 'past', null], queryFn: () => api.meetings.list({ when: 'past' }) });
  const spaces = useSpaces();
  const people = useTeammates();
  const tracks = useQuery({ queryKey: ['all-tracks', (spaces.data ?? []).map(s => s.id).join()], enabled: !!spaces.data,
    queryFn: async () => (await Promise.all((spaces.data ?? []).map(s => api.spaces.get(s.id)))).flatMap(d => d.tracks.map(t => ({ id: t.id, label: `${d.name} · ${t.name}` }))) });
  const [activityId, setActivityId] = useState(params.get('activity') ?? '');
  const [f, setF] = useState({ title: '', trackId: '', date: new Date().toISOString().slice(0, 10), time: '10:00', minutes: '' });
  const [acts, setActs] = useState<{ title: string; ownerId: string; dueDate: string }[]>([{ title: '', ownerId: '', dueDate: '' }]);
  const save = useChange(() => api.meetings.log({
    ...(activityId ? { activityId } : { trackId: f.trackId, title: f.title, startsAt: new Date(`${f.date}T${f.time}:00+03:00`).toISOString() }),
    minutes: f.minutes, actions: acts.filter(a => a.title.trim()).map(a => ({ title: a.title, ownerIds: a.ownerId ? [a.ownerId] : [], dueDate: a.dueDate || null })),
  }));
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(undefined, { onSuccess: r => { toast(`Minutes filed${r.createdActivityIds.length ? `, ${r.createdActivityIds.length} actions added` : ''}.`); onDone(r.activityId); } }); };
  const inp = phone ? 'ph-inp' : 'inp';
  return (
    <form className="col" style={{ gap: 12 }} onSubmit={submit} aria-label="Log a meeting">
      {!phone && <h2 style={{ margin: 0 }}>Log a meeting</h2>}
      <div className="xs mut">Type the minutes and the actions. They are filed on the meeting as you save them, no review needed.</div>
      <label className="fld">Meeting<select className={inp} aria-label="Meeting" value={activityId} onChange={e => setActivityId(e.target.value)}>
        <option value="">A new meeting</option>{(past.data ?? []).map(m => <option key={m.id} value={m.id}>{m.title} ({whenText(m).split(',')[0]})</option>)}</select></label>
      {!activityId && <div className="formgrid">
        <label className="fld">Title<input className={inp} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} required /></label>
        <label className="fld">Track<select className={inp} aria-label="Track" value={f.trackId} onChange={e => setF({ ...f, trackId: e.target.value })} required><option value="">Choose</option>{(tracks.data ?? []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</select></label>
        <label className="fld">Date<input className={inp} type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} required /></label>
        <label className="fld">Time<input className={inp} type="time" value={f.time} onChange={e => setF({ ...f, time: e.target.value })} required /></label>
      </div>}
      <label className="fld">Minutes<textarea className={inp} aria-label="Minutes" rows={8} value={f.minutes} onChange={e => setF({ ...f, minutes: e.target.value })} required placeholder="What was discussed and decided" /></label>
      <div className="col" style={{ gap: 6 }} role="group" aria-label="Actions">
        <span className="k">Actions</span>
        {acts.map((a, i) => (
          <div className="formrow" key={i}>
            <label className="fld">Action<input className={inp} value={a.title} onChange={e => setActs(acts.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Who does what" /></label>
            <label className="fld">Owner<select className={inp} aria-label="Owner" value={a.ownerId} onChange={e => setActs(acts.map((x, j) => (j === i ? { ...x, ownerId: e.target.value } : x)))}><option value="">Me</option>{(people.data ?? []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
            <label className="fld">Due<input className={inp} type="date" value={a.dueDate} onChange={e => setActs(acts.map((x, j) => (j === i ? { ...x, dueDate: e.target.value } : x)))} /></label>
          </div>
        ))}
        <div><button type="button" className="bt sm" onClick={() => setActs([...acts, { title: '', ownerId: '', dueDate: '' }])}>Add an action</button></div>
      </div>
      {save.error ? <ErrorNote error={save.error} /> : null}
      <div><button className={phone ? 'ph-btf' : 'bt pri'} disabled={save.isPending || !f.minutes.trim()}>File the minutes</button></div>
    </form>
  );
}

// ---------- phone ----------

function PhoneMeetings() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const q = useMeetings('all', null);
  const rows = q.data ?? [];
  const groups = useMemo(() => ({
    wait: rows.filter(m => m.minutes === 'wait'),
    filed: rows.filter(m => m.past && (m.minutes === 'ok' || m.minutes === 'processing')).slice(0, 8),
    next: rows.filter(m => !m.past && m.startsAt && new Date(m.startsAt).getTime() - Date.now() < 7 * 86400_000),
  }), [rows]);
  if (id) return <PhoneMeeting id={id} />;
  const row = (m: MeetingRow, right?: string) => (
    <Link key={m.id} className="ph-row" to={`/meetings/${m.id}`} data-meeting={m.title}><span className="ph-rt"><div className="t">{m.title}</div>
      <div className="s">{[whenText(m).replace(/ to \d\d:\d\d$/, ''), m.trackName].join(' · ')}</div></span>{right ? <Tag tone="a">{right}</Tag> : <P.chev />}</Link>
  );
  return (
    <div className="ph-page phm">
      <TopBar back={{ to: '/more', label: 'More' }} />
      <Title title="Meetings" sub={groups.wait.length ? `${groups.wait.length} waiting for review` : 'Minutes are drafted for you to review'} />
      <div className="col" style={{ gap: 8 }}>
        <Link className="ph-btf" to="/record">Record a meeting</Link>
        <button type="button" className="ph-btf sec" onClick={() => setParams({ log: '1' })}>Log a meeting</button>
      </div>
      {q.isLoading && <Loading />}
      {groups.wait.length > 0 && <><Section title="Waiting for review" count={groups.wait.length} /><div className="ph-grp" role="group" aria-label="Waiting for review">{groups.wait.map(m => row(m, 'Review'))}</div></>}
      {groups.filed.length > 0 && <><Section title="Filed" /><div className="ph-grp" role="group" aria-label="Filed">{groups.filed.map(m => row(m))}</div></>}
      <Section title="Next 7 days" />
      <div className="ph-grp" role="group" aria-label="Next 7 days">{groups.next.map(m => row(m))}{!groups.next.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">Nothing in the next 7 days.</div></span></div>}</div>
      <div className="ph-note">The notetaker for Teams and Zoom calls is a later phase. Record in the room for now.</div>
      <AnimatePresence>{params.get('log') === '1' && <PSheet label="Log a meeting" onClose={() => setParams({})}><h2>Log a meeting</h2><LogMeeting phone onDone={a => { setParams({}); nav(`/meetings/${a}`); }} /></PSheet>}</AnimatePresence>
    </div>
  );
}

function PhoneMeeting({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['meeting', id], queryFn: () => api.meetings.get(id), refetchInterval: r => (r.state.data?.minutes === 'processing' ? 4000 : false) });
  const [tab, setTab] = useState<'sum' | 'tr' | 'ask'>('sum');
  const [seek, setSeek] = useState<{ at: number; n: number } | null>(null);
  const onSeek = (at: number) => setSeek(s => ({ at, n: (s?.n ?? 0) + 1 }));
  useSeekParam(onSeek);
  const m = q.data;
  return (
    <div className="ph-page phm mtg">
      <TopBar back={{ to: '/meetings', label: 'Meetings' }} />
      {!m ? (q.error ? <ErrorNote error={q.error} /> : <Loading />) : <>
        <Title eyebrow={whenText(m)} title={m.title} sub={`${m.trackName} · ${m.owner?.name.split(' ')[0] ?? ''}`} tags={<Tag tone={m.minutes === 'wait' ? 'a' : m.minutes === 'ok' ? 'g' : ''}>{MINUTES_STATE[m.minutes][0]}</Tag>} />
        {m.recording && <Player src={m.recording.hasAudio ? api.recordings.audioUrl(m.recording.id) : null} secs={m.recording.durationSecs ?? 0} marks={(m.recording.minutes?.topics ?? []).map(t => t.at)} seekTo={seek} />}
        {m.past || m.recording ? <>
          <div className="gap16" />
          <Seg value={tab} options={[['sum', 'Summary'], ['tr', 'Transcript'], ['ask', 'Ask']] as const} onChange={setTab} label="Meeting" />
          <div className="gap16" />
          {tab === 'sum' && <Summary m={m} onSeek={onSeek} />}
          {tab === 'tr' && <Transcript lines={m.recording?.lines ?? []} rec={m.recording} canName={m.canRecord} onSeek={onSeek} />}
          {tab === 'ask' && <AskMeeting meetingId={m.id} title={m.title} chips={['Summarise the outcome', 'Who owns what?']} />}
          {m.minutes === 'wait' && m.recording?.actionId && <><div className="gap16" /><Link className="ph-btf" to={`/requests?id=${m.recording.actionId}`}>Review minutes</Link></>}
        </> : <Upcoming m={m} />}
      </>}
    </div>
  );
}
